import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { useTenantDatabaseBackupApi } from '@/features/superAdmin/features/tenants/hooks/useTenantDatabaseBackupApi';

type Props = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
};

const DatabaseBackupDialog: FC<Props> = ({ open, onOpenChange }) => {
	const { t } = useTranslation();
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { createAndDownloadBackup, isCreatingBackup } =
		useTenantDatabaseBackupApi();

	const handleConfirm = async () => {
		await createAndDownloadBackup();
		onOpenChange(false);
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(nextOpen) => {
				if (isCreatingBackup && !nextOpen) return;
				onOpenChange(nextOpen);
			}}>
			<DialogContent
				className={cn(isMobile && 'max-h-[90vh] overflow-y-auto')}
				onPointerDownOutside={(e) => {
					if (isCreatingBackup) e.preventDefault();
				}}
				onEscapeKeyDown={(e) => {
					if (isCreatingBackup) e.preventDefault();
				}}>
				<div className='relative flex min-h-0 flex-1 flex-col gap-6'>
					{isCreatingBackup && (
						<div
							className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-2.5xl bg-popover-10/95 p-6'
							aria-busy='true'
							aria-live='polite'>
							<Loader2 className='size-10 animate-spin text-popover-900' />
							<p className='text-center text-sm text-popover-800'>
								{t(
									'main.superAdmin.tenants.components.databaseBackup.inProgressDetail',
								)}
							</p>
						</div>
					)}
					<DialogHeader>
						<DialogTitle>
							{t(
								'main.superAdmin.tenants.components.databaseBackup.dialogTitle',
							)}
						</DialogTitle>
						<DialogDescription>
							{t(
								'main.superAdmin.tenants.components.databaseBackup.dialogDescription',
							)}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className='flex-col sm:flex-row gap-2 sm:justify-end'>
						<Button
							type='button'
							variant='outline'
							disabled={isCreatingBackup}
							onClick={() => onOpenChange(false)}>
							{t(
								'main.superAdmin.tenants.components.databaseBackup.cancel',
							)}
						</Button>
						<Button
							type='button'
							disabled={isCreatingBackup}
							onClick={() => void handleConfirm()}>
							{isCreatingBackup ? (
								<>
									<Loader2 className='mr-2 size-4 animate-spin' />
									{t(
										'main.superAdmin.tenants.components.databaseBackup.inProgress',
									)}
								</>
							) : (
								t(
									'main.superAdmin.tenants.components.databaseBackup.submit',
								)
							)}
						</Button>
					</DialogFooter>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default DatabaseBackupDialog;
